import React, { useState, useEffect, useRef } from 'react';
import {
  format, addMonths, subMonths,
  startOfMonth, endOfMonth,
  startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay
} from 'date-fns';
import { pl, uk, ru } from 'date-fns/locale';

import CalendarIcon from '@/assets/image/calendar-days.svg?react';

interface RescheduleCalendarProps {
  availableDates: Date[];
  selectedDate: Date | null;
  onSelect: (date: Date) => void;
  t?: any;
  lang?: 'pl' | 'uk' | 'ru';
}

const ChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);


export const RescheduleCalendar: React.FC<RescheduleCalendarProps> = ({ availableDates, selectedDate, onSelect, t, lang = 'pl' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate || (availableDates.length > 0 ? availableDates[0] : new Date())
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedDate) setCurrentMonth(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    const handleOut = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOut);
    return () => document.removeEventListener('mousedown', handleOut);
  }, []);

  const monthStart = startOfMonth(currentMonth);
  const days = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 }),
  });
  const isAvailable = (d: Date) => availableDates.some(a => isSameDay(a, d));
  const dateLocale = lang === 'uk' ? uk : (lang === 'ru' ? ru : pl);
  const formattedDate = selectedDate
    ? format(selectedDate, 'd MMMM yyyy', { locale: dateLocale })
    : (t ? t('resch_select_date') : 'Wybierz');

  return (
    <div className="rcal-wrap" ref={containerRef}>
      {/* Nav row trigger */}
      <div className="rcal-nav-row">
        <button
          type="button"
          className="rcal-nav-arrow"
          onClick={e => { e.stopPropagation(); if (selectedDate) { const prev = availableDates.filter(d => d < selectedDate); if (prev.length) onSelect(prev[prev.length - 1]); } }}
        >
          <ChevronLeft />
        </button>
        <button type="button" className="rcal-nav-center" onClick={() => setIsOpen(o => !o)}>
          <CalendarIcon />
          <span>{formattedDate}</span>
        </button>
        <button
          type="button"
          className="rcal-nav-arrow"
          onClick={e => { e.stopPropagation(); if (selectedDate) { const nxt = availableDates.find(d => d > selectedDate); if (nxt) onSelect(nxt); } }}
        >
          <ChevronRight />
        </button>
      </div>

      {/* Calendar popup / inline grid */}
      <div className={`rcal-overlay ${isOpen ? 'open' : ''}`} onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}></div>
      <div className={`rcal-popup ${isOpen ? 'open' : ''}`}>
        {/* Month header */}
        <div className="rcal-popup-header">
          <div className="rcal-month-label">
            {format(currentMonth, 'LLLL yyyy', { locale: dateLocale })}
          </div>
          <div className="rcal-popup-actions">
            <button type="button" className="rcal-icon-btn" onClick={e => { e.stopPropagation(); setCurrentMonth(subMonths(currentMonth, 1)); }}>
              <ChevronLeft />
            </button>
            <button type="button" className="rcal-icon-btn" onClick={e => { e.stopPropagation(); setCurrentMonth(addMonths(currentMonth, 1)); }}>
              <ChevronRight />
            </button>
          </div>
        </div>
        {/* Weekday headers */}
        <div className="rcal-grid-head">
          {(t ? t('weekdays_short') : 'Pon,Wt,śr,czw,Pt,Sob,Niedz').split(',').map((d: string) => <span key={d}>{d}</span>)}
        </div>
        {/* Days */}
        <div className="rcal-grid">
          {days.map((day, i) => {
            const inMonth = isSameMonth(day, monthStart);
            const avail = isAvailable(day);
            const sel = selectedDate && isSameDay(day, selectedDate);
            return (
              <div
                key={i}
                className={`rcal-day${!inMonth ? ' outside' : ''}${avail ? ' avail' : ''}${sel ? ' selected' : ''}`}
                onClick={() => { if (avail) { onSelect(day); setIsOpen(false); } }}
              >
                {format(day, 'd')}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .rcal-wrap {
          position: relative;
          width: 100%;
        }
        .rcal-nav-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          margin-top: 8px;
          margin-bottom: 24px;
        }
        .rcal-nav-arrow {
          width: 32px; height: 32px;
          border-radius: 50%;
          border: none;
          background: transparent;
          color: #361777;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.15s;
        }
        .rcal-nav-arrow:hover { background: rgba(255,255,255,0.5); }
        .rcal-nav-center {
          display: flex; align-items: center; gap: 12px;
          background: none; border: none;
          font-family: 'Poppins', sans-serif;
          font-size: 16px; font-weight: 700;
          color: #361777; cursor: pointer;
        }
        /* Popup/Grid Desktop */
        .rcal-popup {
          background: #fff;
          border-radius: 24px;
          padding: 24px 32px;
          width: 453px;
          margin: 0 auto;
          box-shadow: 0 4px 24px rgba(41,27,82,0.08);
          border: 1px solid #57437E;
          display: block; /* Always visible on desktop */
        }
        .rcal-popup-header {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 24px;
        }
        .rcal-month-label {
          font-family: 'Poppins', sans-serif;
          font-size: 18px; font-weight: 700; color: #361777;
          text-transform: capitalize;
        }
        .rcal-popup-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .rcal-icon-btn {
          width: 32px; height: 32px; border-radius: 50%;
          border: none; background: #F8F6FC;
          color: #361777; display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: background 0.15s;
        }
        .rcal-icon-btn:hover { background: #E8DDF4; }
        .rcal-grid-head {
          display: grid; grid-template-columns: repeat(7,1fr);
          text-align: center;
          font-family: 'Poppins', sans-serif;
          font-size: 13px; font-weight: 600; color: #361777;
          margin-bottom: 12px;
        }
        .rcal-grid {
          display: grid; grid-template-columns: repeat(7,1fr);
          gap: 4px;
        }
        .rcal-day {
          aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
          font-family: 'Poppins', sans-serif;
          font-size: 16px; font-weight: 500;
          color: #CCC; border-radius: 50%; cursor: default;
        }
        .rcal-day.outside { visibility: hidden; }
        .rcal-day.avail { color: #4F525B; cursor: pointer; }
        .rcal-day.avail:hover { background: #F8F6FC; }
        .rcal-day.selected { font-weight: 700; background: #361777; color: #fff !important; }

        @media (max-width: 768px) {
          .rcal-nav-row {
            justify-content: flex-end;
            margin-top: 0;
            margin-bottom: 0;
            gap: 8px;
          }
          .rcal-nav-arrow {
            display: none;
          }
          .rcal-nav-center {
            font-size: 15px;
          }
          .rcal-overlay { display: none; }
          /* Popup Mobile */
          .rcal-popup {
            position: fixed;
            bottom: 0;
            left: 0; transform: translateY(100%);
            width: 100%; border-radius: 32px 32px 0 0; padding: 32px 24px 48px;
            z-index: 100;
            box-shadow: 0 -8px 40px rgba(41,27,82,0.1);
            border: none;
            margin: 0;
            background: #FFFFFF;
            visibility: hidden;
            transition: transform 0.3s ease-out, visibility 0s 0.3s;
          }
          .rcal-popup.open {
            transform: translateY(0);
            visibility: visible;
            transition: transform 0.3s ease-out, visibility 0s;
            display: block;
          }
          .rcal-overlay.open {
            display: block;
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(41, 27, 82, 0.3);
            z-index: 99;
            backdrop-filter: blur(2px);
          }
        }
      `}</style>
    </div>
  );
};
